import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold transition-all cursor-pointer disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none",
  {
    variants: {
      variant: {
        // Primary - зелёный фон, тёмный текст
        default:
          "bg-primary-500 text-neutral-950 glass-blur hover:bg-primary-400 active:bg-primary-700 focus-visible:ring-4 focus-visible:ring-primary-800/22 focus-visible:ring-offset-0 disabled:bg-primary-800/6 disabled:text-primary-800/22",
        primary:
          "bg-primary-500 text-neutral-950 glass-blur hover:bg-primary-400 active:bg-primary-700 focus-visible:ring-4 focus-visible:ring-primary-800/22 focus-visible:ring-offset-0 disabled:bg-primary-800/6 disabled:text-primary-800/22",

        // Secondary - прозрачный зелёный фон, зелёный текст
        secondary:
          "bg-primary-800/12 text-primary-500 glass-blur hover:bg-primary-800/22 active:bg-primary-800/44 focus-visible:ring-4 focus-visible:ring-primary-800/22 focus-visible:ring-offset-0 disabled:bg-neutral-200/5 disabled:text-neutral-200/22",

        // Tertiary - прозрачный белый фон с blur, зелёный текст
        tertiary:
          "bg-neutral-200/5 text-primary-500 glass-blur hover:bg-neutral-200/7 active:bg-neutral-200/12 focus-visible:bg-neutral-200/7 focus-visible:ring-4 focus-visible:ring-neutral-200/12 focus-visible:ring-offset-0 disabled:bg-neutral-200/5 disabled:text-neutral-200/22",

        // Neutral - прозрачный белый фон с blur, серый текст
        neutral:
          "bg-neutral-200/5 text-neutral-200 glass-blur hover:bg-neutral-200/12 active:bg-neutral-200/12 focus-visible:bg-neutral-200/7 focus-visible:ring-4 focus-visible:ring-neutral-200/12 focus-visible:ring-offset-0 disabled:bg-neutral-200/5 disabled:text-neutral-200/22",

        // Negative - прозрачный красный фон, красный текст
        destructive:
          "bg-danger/10 text-danger glass-blur hover:bg-danger/17 active:bg-danger/35 focus-visible:ring-4 focus-visible:ring-danger/25 focus-visible:ring-offset-0 disabled:bg-neutral-200/5 disabled:text-neutral-200/22",

        // White - белый фон, тёмный текст
        white:
          "bg-white text-neutral-950 hover:bg-white/90 active:bg-white/80 focus:border-white/20",

        // Link - как ссылка
        link: "text-primary underline-offset-4 hover:underline p-0 h-auto focus:border-primary/20",
      },

      buttonType: {
        default: "",
        icon: "",
      },
      size: {
        sm: "",
        default: "",
        md: "",
        lg: "",
      },

      rounded: {
        default: "rounded",
        full: "rounded-full",
      },
    },

    compoundVariants: [
      // Default button type
      { buttonType: "default", size: "sm", className: "px-4 py-2 fs-200 [&_svg]:size-5" },
      { buttonType: "default", size: "default", className: "px-6 py-3 fs-200 [&_svg]:size-5" },
      { buttonType: "default", size: "md", className: "px-6 py-3 fs-200 [&_svg]:size-5" },
      { buttonType: "default", size: "lg", className: "px-8 py-4 fs-300 [&_svg]:size-5" },

      // Icon-only button type
      { buttonType: "icon", size: "sm", className: "size-10 p-2 [&_svg]:size-5" },
      { buttonType: "icon", size: "default", className: "size-12 p-3 [&_svg]:size-5" },
      { buttonType: "icon", size: "md", className: "size-12 p-3 [&_svg]:size-5" },
      { buttonType: "icon", size: "lg", className: "size-14 p-4 [&_svg]:size-5" },
    ],

    defaultVariants: {
      variant: "primary",
      buttonType: "default",
      size: "md",
      rounded: "default",
    },
  }
);

const Button = ({
  className,
  variant,
  buttonType,
  size,
  rounded,
  asChild = false,
  icon,
  iconClassName,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    icon?: string | React.ReactNode;
    iconClassName?: string;
  }) => {
  const Comp = asChild ? Slot : "button";

  const iconElement =
    icon && typeof icon === "string" ? (
      <img
        src={icon}
        alt=""
        className={cn("size-5", iconClassName)}
      />
    ) : (
      icon
    );

  // When asChild is true, render only children (Slot expects single child)
  // Icon should be placed inside the child element manually
  if (asChild) {
    return (
      <Comp
        data-slot="button"
        className={cn(
          buttonVariants({ variant, buttonType, size, rounded, className }),
          "select-none"
        )}
        {...props}
      >
        {children}
      </Comp>
    );
  }

  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({ variant, buttonType, size, rounded, className }),
        "select-none"
      )}
      {...props}
    >
      {iconElement}
      {children}
    </Comp>
  );
};

export { Button, buttonVariants };
